import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../../../form/Button';
import { ModalFooter } from '../../../modal/layout/ModalFooter';
import {
    ModalScrollableBody,
    ModalScrollableFooter,
} from '../../../modal/layout/ModalScrollableLayout';
import { NOTIFICATION_VARIANT, useNotifications } from '../../../notifications/Notifications';
import { useProfile } from '../../../../profile';
import type { ImageAttributeEntry } from '../contracts';
import { useAttributeMutations } from '../graphql/useAttributeMutations';
import { ATTRIBUTE_UPLOAD_ACTIONS, AttributeUploadError, uploadAttributeFiles } from '../uploadApi';
import type { AttributeModalContentProps } from './types';

interface ImageAttributeFormValues {
    caption: string;
}

export function AttributeImageModalContent({
    entry,
    entityType,
    entityId,
    onClose,
    onAfterMutation,
    onFormStateChange,
    bodyIntro,
    scrollBodyRef,
}: AttributeModalContentProps<ImageAttributeEntry>) {
    const formId = `attribute-image-${entry.key}`;
    const profile = useProfile();
    const { addNotification } = useNotifications();
    const { setAssetCaption, deleteAsset, isMutating } = useAttributeMutations();
    const [isUploading, setIsUploading] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
    const [isRemoved, setIsRemoved] = useState(false);
    const {
        register,
        handleSubmit,
        reset,
        formState: { isDirty: isCaptionDirty, isSubmitting },
    } = useForm<ImageAttributeFormValues>({
        defaultValues: {
            caption: entry.image?.caption ?? '',
        },
    });
    const isBusy = isSubmitting || isMutating || isUploading;
    const isDirty = isCaptionDirty || pendingFile !== null || isRemoved;
    let previewImage = pendingFile
        ? { url: pendingPreviewUrl, name: pendingFile.name, caption: '' }
        : entry.image;
    if (isRemoved && !pendingFile) {
        previewImage = null;
    }

    useEffect(() => {
        reset({ caption: entry.image?.caption ?? '' });
    }, [entry.image?.caption, entry.image?.id, reset]);

    useEffect(() => {
        if (!pendingFile) {
            setPendingPreviewUrl(null);
            return undefined;
        }

        const url = URL.createObjectURL(pendingFile);
        setPendingPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pendingFile]);

    useEffect(() => {
        onFormStateChange({ isDirty, isBusy });
    }, [isBusy, isDirty, onFormStateChange]);

    function stageReplacement(files: FileList | null) {
        const file = files?.[0];
        if (!file) {
            return;
        }
        setPendingFile(file);
        setIsRemoved(false);
    }

    async function saveImage(values: ImageAttributeFormValues) {
        let savedCaption = values.caption;
        try {
            if (pendingFile) {
                setIsUploading(true);
                const uploadedEntry = await uploadAttributeFiles({
                    action: ATTRIBUTE_UPLOAD_ACTIONS.replaceImage,
                    entityType,
                    entityId,
                    attributeKey: entry.key,
                    valueType: 'image',
                    files: [pendingFile],
                    caption: values.caption,
                    csrfToken: profile.csrfToken,
                });
                if (uploadedEntry.valueType !== 'image') {
                    throw new Error(`replaceAttributeImage returned ${uploadedEntry.valueType}.`);
                }
                savedCaption = uploadedEntry.image?.caption ?? values.caption;
            } else if (isRemoved && entry.image) {
                await deleteAsset({ entityType, entityId, key: entry.key }, entry.image.id);
                savedCaption = '';
            } else if (entry.image) {
                const updatedEntry = await setAssetCaption(
                    { entityType, entityId, key: entry.key },
                    entry.image.id,
                    values.caption
                );
                if (updatedEntry.valueType !== 'image') {
                    throw new Error(
                        `setAttributeAssetCaption returned ${updatedEntry.valueType} for ${entry.key}.`
                    );
                }
                savedCaption = updatedEntry.image?.caption ?? '';
            }

            reset({ caption: savedCaption });
            setPendingFile(null);
            setIsRemoved(false);
            await onAfterMutation();
            addNotification({
                message: gettext('SAVED_SUCCESSFULLY'),
                variant: NOTIFICATION_VARIANT.SUCCESS,
                autoDismiss: true,
            });
        } catch (error) {
            if (error instanceof AttributeUploadError) {
                addNotification({
                    message: error.message,
                    variant: NOTIFICATION_VARIANT.ERROR,
                    autoDismiss: true,
                });
                return;
            }
            throw error;
        } finally {
            setIsUploading(false);
        }
    }

    function stageRemoval() {
        setPendingFile(null);
        setIsRemoved(Boolean(entry.image));
        reset({ caption: '' });
    }

    return (
        <>
            <ModalScrollableBody ref={scrollBodyRef}>
                {bodyIntro}
                <div className="space-y-5 px-6">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        {previewImage ? (
                            <div className="space-y-3">
                                {previewImage.url && (
                                    <img
                                        src={previewImage.url}
                                        alt={previewImage.caption || entry.label}
                                        className="max-h-80 w-full rounded-lg bg-white object-contain ring-1 ring-gray-200"
                                    />
                                )}
                                <div className="flex items-center justify-between gap-3 text-sm text-gray-600">
                                    <span>{previewImage.name}</span>
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        className="px-4 py-2 text-sm"
                                        disabled={isBusy}
                                        onClick={stageRemoval}
                                    >
                                        {gettext('ATTRIBUTE_REMOVE_IMAGE_ACTION')}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">
                                {gettext('ATTRIBUTE_NO_IMAGE_UPLOADED')}
                            </p>
                        )}
                    </div>

                    <label
                        className="block text-sm font-semibold text-gray-700"
                        htmlFor={`${formId}-file`}
                    >
                        {previewImage
                            ? gettext('ATTRIBUTE_REPLACE_IMAGE_FILE_LABEL')
                            : gettext('ATTRIBUTE_ADD_IMAGE_FILE_LABEL')}
                    </label>
                    <input
                        id={`${formId}-file`}
                        type="file"
                        accept="image/*"
                        disabled={isBusy}
                        onChange={(event) => {
                            stageReplacement(event.target.files);
                            event.target.value = '';
                        }}
                        className="file:bg-primary block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
                    />

                    <form
                        id={formId}
                        onSubmit={(event) => void handleSubmit(saveImage)(event)}
                        className="space-y-3"
                    >
                        <label
                            className="block text-sm font-semibold text-gray-700"
                            htmlFor={`${formId}-caption`}
                        >
                            {gettext('ATTRIBUTE_CAPTION_FIELD')}
                        </label>
                        <textarea
                            id={`${formId}-caption`}
                            rows={3}
                            disabled={!previewImage || isBusy}
                            className="focus:border-primary focus:ring-primary/30 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-inner focus:outline-none focus:ring-2 disabled:bg-gray-100"
                            {...register('caption')}
                        />
                    </form>
                </div>
            </ModalScrollableBody>
            <ModalScrollableFooter>
                <ModalFooter
                    formId={formId}
                    onClose={onClose}
                    saveButtonType="submit"
                    isLoading={isBusy}
                    saveDisabled={!isDirty}
                    saveLabel={gettext('ATTRIBUTE_SAVE_CHANGES_ACTION')}
                />
            </ModalScrollableFooter>
        </>
    );
}
